import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { StatsigClientService } from "@core-services/statsig/statsig.service";

// NOTE: Enum values should have exactly the same value as in the LaunchDarkly app (make sure to copy/paste it here)
export enum LD_FLAG {
	SURVEY_DEPOSIT = "broker.surveys.deposit_insights",
	GQL_TRADE_DERIVATIVES = "trade_engine.general.device_invocation.securities_enabled",
	PAYSAFE_MASTERCARD = "broker.psp.paysafe_allowed_deposit_mastercard",
	PAYSAFE_VISA_CREDIT = "broker.psp.paysafe_allowed_deposit_visa_credit_card",
	STOCKS_20_CUSTODY_ACCOUNT_OPENING = "broker.web.stocks_20.custody_account_opening_temporary", // @TODO: use the real LD flag when feature is complete
	STAKING_ENABLED = "broker.staking",
	DEPOSIT_NEW_DEPOSIT_FLOW_ENABLED = "broker.deposit.new_deposit_flow.enabled",
	LEVERAGE = "broker.fe.leverage",
	SAVINGS_PLAN_WIDGET = "CE.FT_savings_plan_widget",
	SAVINGS_INTRO_MODAL = "CE.FT.savings.plan.intro.modal",
	TRADE_SUCCESSFUL_SCREEN_REDESIGN = "trading_flows.web.trade_success_screen_redesign",
	TRANSACTION_SHARE_ENABLED = "trading_flows.web.transaction_share_enabled",
	GQL_TRADE_BCI = "trade_engine.general.device_invocation.bci_enabled",
	ADP_SHARE_ENABLED = "trading_flows.web.asset_share_enabled",
	CARD_CASHBACK = "broker.card.cashback.enabled",
	BUY_AGAIN = "broker.fe.section.trade_again",
	PORTFOLIO_EMPTY_STATE_V3 = "portfolio-empty-state-v3",
	DARK_MODE_ENABLED = "broker.web.dark_mode_enabled",
	PORTFOLIO_GROUPED_PERFORMANCE = "portfolio.grouped_performance_kpi",
	CHECKOUT_FLOW_ENABLED = "broker.fe.checkout",
	AFFILIATES_REFERRALS_REVAMP = "affiliates.general.referrals_revamp",
	PORTFOLIO_NEW_TIME_FILTERS = "portfolio.new_chart_filters",
	PORTFOLIO_CROSS_SELLING_SECTION = "portfolio.cross_selling_section",
	NOTIFICATIONS = "broker.fe.notification_center",
	NEW_GLOBAL_NOTIFICATIONS = "broker.fe.notification_center.global_notifications",
	PORTFOLIO_HIDE_BCI_KPIS = "portfolio.hide-bci-kpis",
	CASH_PLUS = "broker.earn_on_fiat",
	LEVERAGE_CFD = "broker.fe.leverage-cfd-changes",
	DEPOSIT_MULTICHAIN = "broker.payments.cryptocoin.deposit.multichain.enabled",
	WITHDRAWAL_MULTICHAIN = "payments.cryptocoin.withdraw.multichain.enabled",
	APPROPRIATENESS_TEST_V2 = "broker.appropriateness_test.v2",
	PREFERENCE_CENTRE = "martech.communications_preferences_center.enabled",
	BANKID = "cat.bankid_scrive_redirect_sweden_residents",
	NEW_FEATURE_HIGHLIGHT = "broker.fe.new-feature-highlight",
	BITPANDA_STORIES = "broker.fe.stories",
	TAX_WITHHOLDING = "broker.fe.tax_withholding",
	TAX_WITHHOLDING_TRADE = "broker.fe.trade.tax_withholding",
	TAX_WITHHOLDING_SUMMARY_VARIATION = "broker.fe.summary_variation.hide_tax_withheld_amount",
	HIDE_BEST_REWARD_STATUS = "broker.fe.hide_best_rewards_status",
	HOME_BANNER_TYPE = "uk.broker.fe.home_banner_type",
	TAX_SPLIT_DEPOSIT = "broker.fe.tax_withholding.split_deposit",
	UK_FINPROM_GLOBAL_WARNING = "broker.fe.uk_fin_prom_banner.enabled",
	HIDE_PORTFOLIO = "broker.fe.hide.portfolio.amount",
	TW_CONTINUE_ANYAWY_LOG = "broker.fe.tax_withholding_log_continue_anyway",
	LIMIT_ORDERS_BUY = "broker.fe.buy.limit_orders",
	LIMIT_ORDERS_SELL = "broker.fe.sell.limit_orders",
	LIMIT_ORDERS_HOME_ADP = "broker.fe.limit_order_home_adp_redesign",
	DUST_TO_BEST = "broker.fe.dust_to_best",
	BP_CARD_LIMITED_STOCK = "broker.fe.card.order.limited_stock",
	PAYMENTS_FIAT_DEPOSIT_FREE = "payments.fiat.deposit.all.methods.free",
	ADVANCED_TRADING = "broker.web.advanced-trading",
	ASSET_LOGOS_V4 = "broker.be.v4_logos.enabled",
	SECURITY_TOKENS = "broker.fe.security-tokens-category",
	ITB_REWARDS_TOGGLE = "best-rewards-5-years-itb-toggle",
	UK_FINPROM_GLOBAL_RISK_WARNING = "broker.fe.global_risk_warning_banner.enabled",
	UK_FINPROM_BITPANDA_STORIES = "broker.fe.uk_stories.enabled",
	ANTI_PHISHING_CODE = "broker.fe.antiphishing_code",
	UK_FIN_PROM_REDIRECT = "broker.uk_fin_prom.redirect",
	LIMIT_ORDERS_SWITCH_VARIATIONS = "amount_screen_bottom_sheet_variation_on_order_types",
	ANTI_PHISHING_BLOCK_UX_IF_UNSET = "broker.fe.antiphishing_code_block_if_unset",
	FEE_TRANSPARENCY_CRYPTO_ONLY = "frontend_fee_transparency_crypto_only",
	TERMS_AND_CONDITIONS = "broker.fe.terms_and_conditions",
	BP_CARD_NOTIFY_CARD_AVAILABLE = "broker.fe.card.rework.notify_card_available",
	AUTO_STAKING = "fe.autostaking_portfolio",
	FUSION_SELF_CERTIFICATION = "broker.web.fusion.self-certification",
	WEB3_TOKEN_MERGE = "broker.fe.web3_token_merge",
	PANTOS_PID = "broker.fe.pantos_pid",
	BEST_PID = "broker.fe.best_pid",
	VISION_PID = "broker.fe.vision_pid",
	WEB3_TOKEN_MERGE_URLS = "broker.fe.web3_token_merge_urls",
	SWAP_SERVICE_GRAPHQL = "broker.fe.enable.trade_service_swap_offer",
	MIGRATE_SAVINGS_TO_FIAT_ID = "fe-switch-to-use-the-fiat-id-on-savings-plans",
	UNIT_SWITCH_AMOUNT = "unit-switcher-on-amount-screen",
	WEB3_VSN_FEE_BENEFIT = "broker.fe.vsn_fee_benefits"
}

@Injectable({
	providedIn: "root"
})
export class LaunchDarklyService {
	private readonly statsigClientService: StatsigClientService = inject(StatsigClientService);

	/**
	 * @deprecated Use `isActive$()` or `getFeatureConfig$()` instead.
	 */
	public isFeatureActive$(flag: LD_FLAG): Observable<boolean> {
		return this.statsigClientService.isFeatureActive(flag);
	}

	public isActive$(flag: LD_FLAG): Observable<boolean> {
		return this.statsigClientService.isFeatureActive(flag);
	}

	public getFeatureConfig$<TConfig>(flag: LD_FLAG): Observable<TConfig> {
		return this.statsigClientService.getDynamicConfig<TConfig>(flag);
	}
}
